import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { getUserDetails } from '../../storageService';
import { getMilestonefaculty, getMilestoneStudent } from '../../Utils/apiService';
import KenLoader from '../../GlobalComponents/KenLoader';

type FacultyStudent = {
  id: string;
  name: string;
  status: string; // "In Progress", "Completed", etc.
  comments: string;
};

type FacultyGoal = {
  title: string;
  studentsOnTrack: { done: number; total: number };
  completionPercentage: number;
  students: FacultyStudent[];
};

type StudentGoal = {
  title: string;
  studentsOnTrack: { done: number; total: number };
  completionPercentage: number;
  currentValue?: number;
  subGoals: { title: string; status: number; comments: string }[];
};

const EditableComment: React.FC<{ initial: string }> = ({ initial }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(initial);
  return editing ? (
    <Box display="flex" gap={1}>
      <TextField
        size="small"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => setEditing(false)}
        autoFocus
      />
      <IconButton size="small" onClick={() => setEditing(false)}>
        <EditIcon fontSize="small" />
      </IconButton>
    </Box>
  ) : (
    <Typography
      onClick={() => setEditing(true)}
      sx={{ cursor: 'pointer', wordBreak: 'break-word' }}
    >
      {value || 'Add comment'}
    </Typography>
  );
};

const GoalsDetails: React.FC = () => {
  const { goalId = '' } = useParams<{ goalId: string }>();
  const userType = (getUserDetails()?.Type as 'Faculty' | 'Student') || 'Student';

  const [facultyGoal, setFacultyGoal] = useState<FacultyGoal | null>(null);
  const [studentGoal, setStudentGoal] = useState<StudentGoal | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [noDataTimeout, setNoDataTimeout] = useState(false);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setNoDataTimeout(true);
    }, 5000);

    const fetchGoalDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        if (userType === 'Faculty') {
          const response = await getMilestonefaculty(goalId);
          console.log('getMilestonefaculty response:', response);
          console.log('getMilestonefaculty data:', response?.data);

          const students: FacultyStudent[] = (response?.data || []).map((item: any) => ({
            id: item.registrationNumber || '',
            name: item.contactName || '',
            status: item.status || '',
            comments: item.comments || '',
          }));

          const firstItem = response?.data?.[0];
          const completionPercentage = firstItem?.progress ? parseInt(firstItem.progress) : 0;
          
          setFacultyGoal({
            title: 'Milestone Progress',
            studentsOnTrack: {
              done: students.filter((s) => s.status === 'Completed').length,
              total: students.length,
            },
            completionPercentage: completionPercentage,
            students,
          });
        } else {
          const response = await getMilestoneStudent(goalId);

          const subGoals = (response?.data || []).map((item: any) => ({
            title: item.Name || 'Untitled',
            status: 0,
            comments: item.Comments || item.Comments__c || '',
          }));

          const currentValue = response?.data?.[0]?.CurrentValue
            ? Number(response.data[0].CurrentValue)
            : 0;

          setStudentGoal({
            title: 'Student Milestone Details',
            studentsOnTrack: { done: 0, total: subGoals.length },
            completionPercentage: currentValue,
            currentValue,
            subGoals,
          });
        }
      } catch (err: any) {
        setError(err.message || 'Unknown error');
      } finally {
        clearTimeout(timeoutId);
        setLoading(false);
      }
    };

    if (goalId) {
      fetchGoalDetails();
    }

    return () => clearTimeout(timeoutId);
  }, [goalId, userType]);

  if (loading) return <KenLoader />;
  if (!facultyGoal && !studentGoal && noDataTimeout) return <Typography>No data available</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  const title = facultyGoal?.title || studentGoal?.title || 'Goal Details';

  const facultyView = facultyGoal && (
    <>
      <Box display="flex" gap={2} mb={3}>
        <Box bgcolor="#e0e7ff" p={2} borderRadius={2} minWidth={250}>
          <Typography fontWeight="500" variant="subtitle1">
            {facultyGoal.studentsOnTrack.done}/{facultyGoal.studentsOnTrack.total}
          </Typography>
          <Typography variant="caption">Students on track</Typography>
        </Box>
        <Box bgcolor="#e0e7ff" p={2} borderRadius={2} minWidth={250}>
          <Typography variant="subtitle1" color="primary" fontWeight="500">
            {facultyGoal.completionPercentage}%
          </Typography>
          <Typography variant="caption">Completion percentage</Typography>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID number</TableCell>
              <TableCell>Student name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {facultyGoal.students.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{s.id}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>
                  <Typography
                    color={s.status === 'Completed' ? 'primary' : 'warning.main'}
                    fontWeight={500}
                  >
                    {s.status}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={s.comments} placement="top">
                    <Typography
                      // sx={{ 
                      //   cursor: 'pointer', 
                      //   wordBreak: 'break-word',
                      //   maxWidth: '200px',
                      //   overflow: 'hidden',
                      //   textOverflow: 'ellipsis',
                      //   whiteSpace: 'nowrap'
                      // }}
                    >
                      {s.comments || 'No comments'}
                    </Typography>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const studentView = studentGoal && (
    <>
      <Box display="flex" gap={2} mb={3}>
        <Box bgcolor="#e0e7ff" p={2} borderRadius={2} minWidth={250}>
          <Typography variant="subtitle1" color="primary" fontWeight="500">
            {studentGoal.currentValue ?? 0}%
          </Typography>
          <Typography variant="caption">Completion percentage</Typography>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>SubGoals</TableCell>
              <TableCell>Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {studentGoal.subGoals.map((sg, idx) => (
              <TableRow key={idx}>
                <TableCell>{sg.title}</TableCell>
                <TableCell>{sg.comments}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  return (
    <Box sx={{ p: 3, bgcolor: '#fff', borderRadius: 2, height: 'calc(81vh)', overflowY: 'auto' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">{title}</Typography>
      </Box>
      {userType === 'Faculty' ? facultyView : studentView}
    </Box>
  );
};

export default GoalsDetails;
